import { subjectTotalError } from './subject-total';
import {
  beginWrongNoteRelearning,
  addCalendarDays,
  calculateDDayAt,
  cumulativeMilestoneCandidates,
  materializePlanVersion,
  milestoneReconciliation,
  studyLogMilestoneDescriptors,
  studyDateAt,
  type Exam,
  type Instant,
  type LocalDate,
  type Milestone,
  type MogakSession,
  type PhotoAsset,
  type Plan,
  type PlanDraft,
  type PlanItem,
  type StudyLog,
  type Subject,
  type UUID,
  type WrongNote,
} from '@hoedokloop/domain';
import type { RepositoryWrite } from '@hoedokloop/platform-ports';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { usePlatform } from '../../platform/platform-context';
import {
  countBucket,
  dDayBucket,
  roundBucket,
  sendProductEvent,
} from '../analytics/product-events';

export type SubjectDraft = {
  name: string;
  totalUnits: number | null;
  unitLabel: Subject['unitLabel'];
  customUnitLabel?: string;
  existingRound?: number;
  existingUnitEnd?: number;
};

export type ExamDraft = {
  title: string;
  examDate?: LocalDate | null | undefined;
  timezone: string;
  subjects: SubjectDraft[];
  presetId?: string;
  stage?: string;
  registrationWindows?: Exam['registrationWindows'];
};

export type ExamUpdateDraft = Pick<Exam, 'title' | 'examDate' | 'timezone'> & {
  result: NonNullable<Exam['result']>;
  subjects: Array<
    Pick<Subject, 'id' | 'name' | 'totalUnits' | 'unitLabel' | 'sortOrder'> & {
      customUnitLabel?: string;
    }
  >;
};

export type StudyLogDraft = Pick<
  StudyLog,
  'examId' | 'subjectId' | 'studyDate' | 'round' | 'unitStart' | 'unitEnd' | 'source'
> & {
  id: UUID;
  minutes?: number;
  memo?: string;
};

export type WrongNoteDraft = {
  id: UUID;
  examId: UUID;
  subjectId: UUID;
  text: string;
  memo?: string;
  tags: string[];
  photo?: Omit<PhotoAsset, 'id' | 'createdAt' | 'deletedAt'>;
  removePhoto?: boolean;
};

type StudyState = {
  status: 'loading' | 'ready' | 'error';
  exams: Exam[];
  subjects: Subject[];
  logs: StudyLog[];
  notes: WrongNote[];
  plans: Plan[];
  planItems: PlanItem[];
  milestones: Milestone[];
  mogakSessions: MogakSession[];
  archivedExams: Exam[];
  deletedLogs: StudyLog[];
  deletedNotes: WrongNote[];
  errorReference?: string;
};

type PendingPlanRefresh = { operation: 'save' | 'restore'; version: number };
type PendingLogRefresh = {
  operation: 'save' | 'update' | 'batch' | 'delete' | 'restore';
  count: number;
};

type StudyContextValue = StudyState & {
  pendingPlanRefresh: PendingPlanRefresh | undefined;
  pendingLogRefresh: PendingLogRefresh | undefined;
  recentAchievements: AchievementNotice[];
  dismissAchievements: () => void;
  reload: () => Promise<boolean>;
  createExam: (draft: ExamDraft) => Promise<Exam | undefined>;
  updateExam: (id: UUID, draft: ExamUpdateDraft) => Promise<Exam | undefined>;
  archiveExam: (id: UUID) => Promise<boolean>;
  restoreExam: (id: UUID) => Promise<boolean>;
  saveLogBatch: (drafts: StudyLogDraft[]) => Promise<boolean>;
  saveLog: (draft: StudyLogDraft) => Promise<SaveLogResult | undefined>;
  updateLog: (
    id: UUID,
    draft: Omit<StudyLogDraft, 'id' | 'source'>,
  ) => Promise<StudyLog | undefined>;
  deleteLog: (id: UUID) => Promise<boolean>;
  restoreLog: (id: UUID) => Promise<boolean>;
  saveNote: (draft: WrongNoteDraft) => Promise<WrongNote | undefined>;
  updateNote: (id: UUID, draft: Omit<WrongNoteDraft, 'id'>) => Promise<WrongNote | undefined>;
  deleteNote: (id: UUID) => Promise<boolean>;
  restoreNote: (id: UUID) => Promise<boolean>;
  reviewNote: (
    id: UUID,
    action: 'review' | 'master' | 'relearn' | 'remembered' | 'uncertain' | 'forgotten',
  ) => Promise<boolean>;
  startReviewSession: (mode: 'random' | 'subject' | 'unmastered', count: number) => void;
  // These mutation results acknowledge the write; pendingPlanRefresh owns read-back.
  confirmPlan: (draft: PlanDraft) => Promise<Plan | undefined>;
  restorePlan: (id: UUID) => Promise<boolean>;
  completePlanItem: (id: UUID, done: boolean) => Promise<boolean>;
  saveMogakSession: (session: MogakSession) => Promise<boolean>;
  getPhoto: (id: UUID) => Promise<PhotoAsset | undefined>;
};

export type AchievementNotice = {
  id: UUID;
  kind: 'daily_stamp' | 'round_completed';
  subjectName?: string;
  round?: number;
};

export type SaveLogResult = {
  log: StudyLog;
  achievements: AchievementNotice[];
};

const StudyContext = createContext<StudyContextValue | undefined>(undefined);

function now(): Instant {
  return new Date().toISOString() as Instant;
}

function id(): UUID {
  return crypto.randomUUID() as UUID;
}

function activeByUpdatedAt<Value extends { updatedAt: Instant; deletedAt?: Instant }>(
  values: Value[],
): Value[] {
  return values
    .filter((value) => value.deletedAt === undefined)
    .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt));
}

export function StudyProvider({ children }: { children: ReactNode }) {
  const { studyRepository, errors, analytics, studyCapabilities } = usePlatform();
  const purgeAttempted = useRef(false);
  const [pendingPlanRefresh, setPendingPlanRefresh] = useState<PendingPlanRefresh>();
  const [pendingLogRefresh, setPendingLogRefresh] = useState<PendingLogRefresh>();
  const [recentAchievements, setRecentAchievements] = useState<AchievementNotice[]>([]);
  const dismissAchievements = useCallback(() => setRecentAchievements([]), []);
  const [state, setState] = useState<StudyState>({
    status: 'loading',
    exams: [],
    subjects: [],
    logs: [],
    notes: [],
    plans: [],
    planItems: [],
    milestones: [],
    mogakSessions: [],
    archivedExams: [],
    deletedLogs: [],
    deletedNotes: [],
  });

  const reload = useCallback(async (): Promise<boolean> => {
    try {
      const opened = await studyRepository.open();
      if (!opened.ok) {
        const report = errors.capture(new Error('Study repository open failed'), {
          boundary: 'study_repository',
          reason: opened.reason,
        });
        setState((current) => ({
          ...current,
          status: 'error',
          ...(report.ok ? { errorReference: report.value.referenceId } : {}),
        }));
        return false;
      }
      if (!purgeAttempted.current) {
        purgeAttempted.current = true;
        const boundary = new Date(Date.now() - 30 * 24 * 60 * 60 * 1_000).toISOString() as Instant;
        const purged = await studyRepository.purgeTrash(boundary);
        if (!purged.ok) {
          errors.capture(new Error('Study trash purge failed'), {
            boundary: 'study_repository',
            feature: 'trash_retention',
            reason: purged.reason,
          });
        }
      }
      const [
        examResult,
        subjectResult,
        logResult,
        noteResult,
        planResult,
        planItemResult,
        milestoneResult,
        mogakSessionResult,
      ] = await Promise.all([
        studyRepository.list('exams', { includeDeleted: true }),
        studyRepository.list('subjects'),
        studyRepository.list('studyLogs', { includeDeleted: true }),
        studyRepository.list('wrongNotes', { includeDeleted: true }),
        studyRepository.list('plans'),
        studyRepository.list('planItems'),
        studyRepository.list('milestones'),
        studyRepository.list('mogakSessions'),
      ]);
      if (
        !examResult.ok ||
        !subjectResult.ok ||
        !logResult.ok ||
        !noteResult.ok ||
        !planResult.ok ||
        !planItemResult.ok ||
        !milestoneResult.ok ||
        !mogakSessionResult.ok
      ) {
        const report = errors.capture(new Error('Study repository read failed'), {
          boundary: 'study_repository',
        });
        setState((current) => ({
          ...current,
          status: 'error',
          ...(report.ok ? { errorReference: report.value.referenceId } : {}),
        }));
        return false;
      }
      const allExams = examResult.value;
      const allLogs = logResult.value;
      const allNotes = noteResult.value;
      const activeExamIds = new Set(
        allExams.filter((exam) => exam.deletedAt === undefined).map((exam) => exam.id),
      );
      setState({
        status: 'ready',
        exams: activeByUpdatedAt(allExams),
        subjects: activeByUpdatedAt(subjectResult.value),
        logs: activeByUpdatedAt(allLogs).filter((log) => activeExamIds.has(log.examId)),
        notes: activeByUpdatedAt(allNotes).filter((note) => activeExamIds.has(note.examId)),
        plans: planResult.value
          .filter((plan) => activeExamIds.has(plan.examId))
          .sort((left, right) => right.generatedAt.localeCompare(left.generatedAt)),
        planItems: planItemResult.value,
        milestones: milestoneResult.value,
        mogakSessions: mogakSessionResult.value,
        archivedExams: allExams.filter((exam) => exam.deletedAt !== undefined),
        deletedLogs: allLogs.filter((log) => log.deletedAt !== undefined),
        deletedNotes: allNotes.filter((note) => note.deletedAt !== undefined),
      });
      setPendingPlanRefresh(undefined);
      setPendingLogRefresh(undefined);
      return true;
    } catch (error) {
      const report = errors.capture(
        error instanceof Error ? error : new Error('Study repository read failed'),
        { boundary: 'study_repository', feature: 'reload' },
      );
      setState((current) => ({
        ...current,
        status: 'error',
        ...(report.ok ? { errorReference: report.value.referenceId } : {}),
      }));
      return false;
    }
  }, [errors, studyRepository]);

  useEffect(() => {
    let active = true;
    void reload().then((loaded) => {
      if (!active || loaded) return;
    });
    return () => {
      active = false;
    };
  }, [reload]);

  const createExam = useCallback(
    async (draft: ExamDraft): Promise<Exam | undefined> => {
      if (!draft.examDate && !studyCapabilities?.undatedExamCreation) return undefined;
      const timestamp = now();
      const exam: Exam = {
        id: id(),
        title: draft.title.trim(),
        ...(draft.presetId ? { presetId: draft.presetId } : {}),
        ...(draft.stage ? { stage: draft.stage } : {}),
        ...(draft.registrationWindows ? { registrationWindows: draft.registrationWindows } : {}),
        examDate: draft.examDate || null,
        timezone: draft.timezone,
        result: 'unknown',
        createdAt: timestamp,
        updatedAt: timestamp,
        revision: 1,
      };
      const subjects: Subject[] = draft.subjects.map((subject, index) => ({
        id: id(),
        examId: exam.id,
        name: subject.name.trim(),
        totalUnits: subject.totalUnits,
        unitLabel: subject.unitLabel,
        ...(subject.customUnitLabel === undefined
          ? {}
          : { customUnitLabel: subject.customUnitLabel.trim() }),
        sortOrder: index,
        createdAt: timestamp,
        updatedAt: timestamp,
        revision: 1,
      }));
      const importedLogs: StudyLog[] = subjects.flatMap((subject, index) => {
        const source = draft.subjects[index];
        if (!source?.existingUnitEnd) return [];
        return [
          {
            id: id(),
            examId: exam.id,
            subjectId: subject.id,
            studyDate: studyDateAt(timestamp, draft.timezone, 4),
            round: source.existingRound ?? 1,
            unitStart: 1,
            unitEnd: source.existingUnitEnd,
            source: 'onboarding_import',
            createdAt: timestamp,
            updatedAt: timestamp,
            revision: 1,
          },
        ];
      });
      const saved = await studyRepository.putBatch([
        { store: 'exams', value: exam },
        ...subjects.map((subject) => ({ store: 'subjects' as const, value: subject })),
        ...importedLogs.map((log) => ({ store: 'studyLogs' as const, value: log })),
      ]);
      if (!saved.ok) {
        errors.capture(new Error('Exam graph write failed'), {
          boundary: 'study_repository',
          feature: 'onboarding',
          reason: saved.reason,
        });
        return undefined;
      }
      await reload();
      return exam;
    },
    [errors, reload, studyRepository, studyCapabilities],
  );

  const updateExam = useCallback(
    async (examId: UUID, draft: ExamUpdateDraft): Promise<Exam | undefined> => {
      if (!draft.examDate && !studyCapabilities?.undatedExamCreation) return undefined;
      const current = await studyRepository.get('exams', examId);
      if (!current.ok || !current.value || current.value.deletedAt) return undefined;
      const timestamp = now();
      const exam: Exam = {
        ...current.value,
        title: draft.title.trim(),
        examDate: draft.examDate || null,
        timezone: draft.timezone,
        result: draft.result,
        updatedAt: timestamp,
        revision: current.value.revision + 1,
      };
      const subjectWrites: Subject[] = [];
      for (const subjectDraft of draft.subjects) {
        const subjectResult = await studyRepository.get('subjects', subjectDraft.id);
        if (
          !subjectResult.ok ||
          !subjectResult.value ||
          subjectResult.value.examId !== examId ||
          subjectResult.value.deletedAt
        ) {
          return undefined;
        }
        const currentLogs = await studyRepository.list('studyLogs', { subjectId: subjectDraft.id });
        if (
          !currentLogs.ok ||
          subjectTotalError(subjectResult.value, subjectDraft.totalUnits, currentLogs.value)
        )
          return undefined;
        const subject: Subject = {
          ...subjectResult.value,
          name: subjectDraft.name.trim(),
          totalUnits: subjectDraft.totalUnits,
          unitLabel: subjectDraft.unitLabel,
          sortOrder: subjectDraft.sortOrder,
          updatedAt: timestamp,
          revision: subjectResult.value.revision + 1,
        };
        if (subjectDraft.customUnitLabel?.trim()) {
          subject.customUnitLabel = subjectDraft.customUnitLabel.trim();
        } else {
          delete subject.customUnitLabel;
        }
        subjectWrites.push(subject);
      }
      const saved = await studyRepository.putBatch([
        { store: 'exams', value: exam },
        ...subjectWrites.map((subject) => ({ store: 'subjects' as const, value: subject })),
      ]);
      if (!saved.ok) {
        errors.capture(new Error('Exam graph update failed'), {
          boundary: 'study_repository',
          feature: 'exam_management',
          reason: saved.reason,
        });
        return undefined;
      }
      await reload();
      return exam;
    },
    [errors, reload, studyRepository, studyCapabilities],
  );

  const archiveExam = useCallback(
    async (examId: UUID): Promise<boolean> => {
      const result = await studyRepository.remove('exams', examId, now());
      if (!result.ok) return false;
      return reload();
    },
    [reload, studyRepository],
  );

  const restoreExam = useCallback(
    async (examId: UUID): Promise<boolean> => {
      const current = await studyRepository.get('exams', examId);
      if (!current.ok || !current.value?.deletedAt) return false;
      const restored = { ...current.value };
      delete restored.deletedAt;
      restored.revision += 1;
      restored.updatedAt = now();
      const result = await studyRepository.put('exams', restored);
      if (!result.ok) return false;
      return reload();
    },
    [reload, studyRepository],
  );

  const awardMilestonesForLog = useCallback(
    async (log: StudyLog): Promise<AchievementNotice[]> => {
      try {
        if (log.source === 'onboarding_import' || log.deletedAt) return [];
        const [subjectResult, logResult, milestoneResult] = await Promise.all([
          studyRepository.get('subjects', log.subjectId),
          studyRepository.list('studyLogs', { subjectId: log.subjectId }),
          studyRepository.list('milestones', { examId: log.examId }),
        ]);
        if (!subjectResult.ok || !subjectResult.value || !logResult.ok || !milestoneResult.ok) {
          errors.capture(new Error('Milestone inputs unavailable'), {
            boundary: 'study_repository',
            feature: 'milestone',
          });
          return [];
        }
        const candidates =
          subjectResult.value.totalUnits === null
            ? [{ key: `daily_stamp:${log.studyDate}` as const, kind: 'daily_stamp' as const }]
            : studyLogMilestoneDescriptors(log, logResult.value, subjectResult.value.totalUnits);
        const existing = new Set(milestoneResult.value.map((milestone) => milestone.key));
        const awarded: AchievementNotice[] = [];
        for (const descriptor of candidates) {
          const { key } = descriptor;
          if (existing.has(key)) continue;
          const earnedAt = now();
          const milestone: Milestone = {
            id: id(),
            key,
            examId: log.examId,
            earnedAt,
          };
          const saved = await studyRepository.put('milestones', milestone);
          if (saved.ok) {
            existing.add(key);
            awarded.push({
              id: milestone.id,
              kind: descriptor.kind,
              ...(descriptor.kind === 'round_completed'
                ? { subjectName: subjectResult.value.name, round: descriptor.round }
                : {}),
            });
            continue;
          }
          errors.capture(new Error('Milestone write failed'), {
            boundary: 'study_repository',
            feature: 'milestone',
            reason: saved.reason,
          });
        }
        return awarded;
      } catch (error) {
        errors.capture(error instanceof Error ? error : new Error('Milestone inputs unavailable'), {
          boundary: 'study_repository',
          feature: 'milestone',
        });
        return [];
      }
    },
    [errors, studyRepository],
  );

  const saveLog = useCallback(
    async (draft: StudyLogDraft): Promise<SaveLogResult | undefined> => {
      const wasFirstLog = state.logs.length === 0;
      const timestamp = now();
      const log: StudyLog = {
        ...draft,
        ...(draft.memo?.trim() ? { memo: draft.memo.trim() } : {}),
        createdAt: timestamp,
        updatedAt: timestamp,
        revision: 1,
      };
      const saved = await studyRepository.put('studyLogs', log);
      if (!saved.ok) {
        errors.capture(new Error('Study log write failed'), {
          boundary: 'study_repository',
          feature: 'study_log',
          reason: saved.reason,
        });
        return undefined;
      }
      const awarded = await awardMilestonesForLog(saved.value);
      if (awarded.length > 0) setRecentAchievements(awarded);
      const exam = state.exams.find((candidate) => candidate.id === saved.value.examId);
      if (exam) {
        const properties = {
          source: saved.value.source,
          roundBucket: roundBucket(saved.value.round),
          dDayBucket: dDayBucket(calculateDDayAt(timestamp, exam.timezone, exam.examDate).days),
        } as const;
        sendProductEvent(analytics, 'study_log_saved', properties);
        if (analytics.consent() === 'granted') {
          const date = saved.value.studyDate;
          const weekday = new Date(`${date}T00:00:00Z`).getUTCDay();
          const week = addCalendarDays(date, -((weekday + 6) % 7));
          const weekLogs = await studyRepository.list('studyLogs');
          if (
            weekLogs.ok &&
            new Set(
              weekLogs.value
                .filter((log) => log.studyDate >= week && log.studyDate <= addCalendarDays(week, 6))
                .map((log) => log.studyDate),
            ).size >= 3
          ) {
            try {
              const key = `hoedokloop:three-days:${week}`;
              if (!localStorage.getItem(key)) {
                sendProductEvent(analytics, 'weekly_three_days', {});
                localStorage.setItem(key, '1');
              }
            } catch {
              /* No durable deduplication: do not synthesize repeat events. */
            }
          }
        }

        if (wasFirstLog) {
          sendProductEvent(analytics, 'first_log_saved', { source: saved.value.source });
        }
        if (awarded.some(({ kind }) => kind === 'round_completed')) {
          sendProductEvent(analytics, 'round_completed', {
            roundBucket: properties.roundBucket,
            dDayBucket: properties.dDayBucket,
          });
        }
      }
      if (!(await reload())) setPendingLogRefresh({ operation: 'save', count: 1 });
      return { log: saved.value, achievements: awarded };
    },
    [
      analytics,
      awardMilestonesForLog,
      errors,
      reload,
      state.exams,
      state.logs.length,
      studyRepository,
    ],
  );

  const saveLogBatch = useCallback(
    async (drafts: StudyLogDraft[]): Promise<boolean> => {
      if (!drafts.length) return false;
      const timestamp = now();
      const entries: StudyLog[] = drafts.map((draft) => ({
        ...draft,
        createdAt: timestamp,
        updatedAt: timestamp,
        revision: 1,
      }));
      const saved = await studyRepository.putBatch(
        entries.map((value) => ({ store: 'studyLogs', value })),
      );
      if (!saved.ok) return false;
      const notices: AchievementNotice[] = [];
      for (const log of entries) {
        notices.push(...(await awardMilestonesForLog(log)));
        sendProductEvent(analytics, 'study_log_saved', {
          source: log.source,
          roundBucket: roundBucket(log.round),
          dDayBucket: dDayBucket(
            calculateDDayAt(
              timestamp,
              state.exams.find((exam) => exam.id === log.examId)!.timezone,
              state.exams.find((exam) => exam.id === log.examId)!.examDate,
            ).days,
          ),
        });
      }
      setRecentAchievements(notices);
      if (!(await reload())) setPendingLogRefresh({ operation: 'batch', count: entries.length });
      return true;
    },
    [studyRepository, awardMilestonesForLog, analytics, reload, state.exams],
  );

  const deleteLog = useCallback(
    async (logId: UUID): Promise<boolean> => {
      const log = await studyRepository.get('studyLogs', logId);
      if (!log.ok || !log.value) return false;
      const timestamp = now();
      const writes: RepositoryWrite[] = [
        {
          store: 'studyLogs',
          value: {
            ...log.value,
            deletedAt: timestamp,
            updatedAt: timestamp,
            revision: log.value.revision + 1,
          },
        },
      ];
      if (log.value.planItemId) {
        const item = await studyRepository.get('planItems', log.value.planItemId);
        if (!item.ok) return false;
        if (item.value)
          writes.push({ store: 'planItems', value: { ...item.value, status: 'planned' } });
      }
      const removed = await studyRepository.putBatch(writes);
      if (!removed.ok) return false;
      if (!(await reload())) setPendingLogRefresh({ operation: 'delete', count: 1 });
      return true;
    },
    [reload, studyRepository],
  );

  const updateLog = useCallback(
    async (
      logId: UUID,
      draft: Omit<StudyLogDraft, 'id' | 'source'>,
    ): Promise<StudyLog | undefined> => {
      const current = await studyRepository.get('studyLogs', logId);
      if (!current.ok || !current.value || current.value.deletedAt) return undefined;
      const timestamp = now();
      const updated: StudyLog = {
        ...current.value,
        ...draft,
        ...(draft.memo?.trim() ? { memo: draft.memo.trim() } : {}),
        updatedAt: timestamp,
        revision: current.value.revision + 1,
      };
      if (!draft.memo?.trim()) delete updated.memo;
      const saved = await studyRepository.put('studyLogs', updated);
      if (!saved.ok) {
        errors.capture(new Error('Study log update failed'), {
          boundary: 'study_repository',
          feature: 'study_log',
          reason: saved.reason,
        });
        return undefined;
      }
      const awarded = await awardMilestonesForLog(saved.value);
      if (awarded.length > 0) setRecentAchievements(awarded);
      if (!(await reload())) setPendingLogRefresh({ operation: 'update', count: 1 });
      return saved.value;
    },
    [awardMilestonesForLog, errors, reload, studyRepository],
  );

  const restoreLog = useCallback(
    async (logId: UUID): Promise<boolean> => {
      const result = await studyRepository.get('studyLogs', logId);
      if (!result.ok || !result.value?.deletedAt) return false;
      const existing = { ...result.value };
      delete existing.deletedAt;
      const value: StudyLog = { ...existing, revision: existing.revision + 1, updatedAt: now() };
      const writes: RepositoryWrite[] = [{ store: 'studyLogs', value }];
      if (value.planItemId) {
        const item = await studyRepository.get('planItems', value.planItemId);
        if (!item.ok) return false;
        if (item.value)
          writes.push({ store: 'planItems', value: { ...item.value, status: 'done' } });
      }
      const restored = await studyRepository.putBatch(writes);
      if (!restored.ok) return false;
      const awarded = await awardMilestonesForLog(value);
      if (awarded.length > 0) setRecentAchievements(awarded);
      if (!(await reload())) setPendingLogRefresh({ operation: 'restore', count: 1 });
      return true;
    },
    [awardMilestonesForLog, reload, studyRepository],
  );

  const saveNote = useCallback(
    async (draft: WrongNoteDraft): Promise<WrongNote | undefined> => {
      const timestamp = now();
      const { photo, removePhoto, ...noteDraft } = draft;
      if (removePhoto) return undefined;
      const photoAsset: PhotoAsset | undefined = photo
        ? {
            ...photo,
            id: id(),
            createdAt: timestamp,
          }
        : undefined;
      const note: WrongNote = {
        ...noteDraft,
        text: noteDraft.text.trim(),
        ...(noteDraft.memo?.trim() ? { memo: noteDraft.memo.trim() } : {}),
        ...(photoAsset ? { photoAssetId: photoAsset.id } : {}),
        tags: [
          ...new Set(noteDraft.tags.map((tag) => tag.normalize('NFC').trim()).filter(Boolean)),
        ],
        reviewCount: 0,
        createdAt: timestamp,
        updatedAt: timestamp,
        revision: 1,
      };
      const saved = photoAsset
        ? await studyRepository.putBatch([
            { store: 'photoAssets', value: photoAsset },
            { store: 'wrongNotes', value: note },
          ])
        : await studyRepository.put('wrongNotes', note);
      if (!saved.ok) return undefined;
      sendProductEvent(analytics, 'wrong_note_saved', {
        hasPhoto: photoAsset !== undefined,
        countBucket: countBucket(state.notes.length + 1),
      });
      await reload();
      return note;
    },
    [analytics, reload, state.notes.length, studyRepository],
  );

  const updateNote = useCallback(
    async (noteId: UUID, draft: Omit<WrongNoteDraft, 'id'>): Promise<WrongNote | undefined> => {
      const current = await studyRepository.get('wrongNotes', noteId);
      if (!current.ok || !current.value || current.value.deletedAt) return undefined;
      const { photo, removePhoto = false, ...noteDraft } = draft;
      const timestamp = now();
      const replacementPhoto: PhotoAsset | undefined = photo
        ? {
            ...photo,
            id: id(),
            createdAt: timestamp,
          }
        : undefined;
      const updated: WrongNote = {
        ...current.value,
        ...noteDraft,
        text: noteDraft.text.trim(),
        tags: [
          ...new Set(noteDraft.tags.map((tag) => tag.normalize('NFC').trim()).filter(Boolean)),
        ],
        ...(replacementPhoto ? { photoAssetId: replacementPhoto.id } : {}),
        updatedAt: timestamp,
        revision: current.value.revision + 1,
      };
      if (removePhoto && !replacementPhoto) delete updated.photoAssetId;
      if (noteDraft.memo?.trim()) updated.memo = noteDraft.memo.trim();
      else delete updated.memo;
      const writes: RepositoryWrite[] = [{ store: 'wrongNotes', value: updated }];
      if (replacementPhoto) writes.unshift({ store: 'photoAssets', value: replacementPhoto });
      if ((replacementPhoto || removePhoto) && current.value.photoAssetId) {
        const previous = await studyRepository.get('photoAssets', current.value.photoAssetId);
        if (!previous.ok || !previous.value) return undefined;
        writes.push({
          store: 'photoAssets',
          value: { ...previous.value, deletedAt: timestamp },
        });
      }
      const saved =
        writes.length > 1
          ? await studyRepository.putBatch(writes)
          : await studyRepository.put('wrongNotes', updated);
      if (!saved.ok) return undefined;
      await reload();
      return updated;
    },
    [reload, studyRepository],
  );

  const deleteNote = useCallback(
    async (noteId: UUID): Promise<boolean> => {
      const current = await studyRepository.get('wrongNotes', noteId);
      if (!current.ok || !current.value || current.value.deletedAt) return false;
      const timestamp = now();
      const deletedNote: WrongNote = {
        ...current.value,
        deletedAt: timestamp,
        updatedAt: timestamp,
        revision: current.value.revision + 1,
      };
      const writes: RepositoryWrite[] = [{ store: 'wrongNotes', value: deletedNote }];
      if (current.value.photoAssetId) {
        const [allNotes, photo] = await Promise.all([
          studyRepository.list('wrongNotes', { includeDeleted: true }),
          studyRepository.get('photoAssets', current.value.photoAssetId),
        ]);
        const hasOtherReference =
          allNotes.ok &&
          allNotes.value.some(
            (note) =>
              note.id !== noteId &&
              note.deletedAt === undefined &&
              note.photoAssetId === current.value?.photoAssetId,
          );
        if (photo.ok && photo.value && !hasOtherReference) {
          writes.push({ store: 'photoAssets', value: { ...photo.value, deletedAt: timestamp } });
        }
      }
      const result = await studyRepository.putBatch(writes);
      if (!result.ok) return false;
      return reload();
    },
    [reload, studyRepository],
  );

  const restoreNote = useCallback(
    async (noteId: UUID): Promise<boolean> => {
      const current = await studyRepository.get('wrongNotes', noteId);
      if (!current.ok || !current.value?.deletedAt) return false;
      const restored = { ...current.value };
      delete restored.deletedAt;
      restored.revision += 1;
      restored.updatedAt = now();
      const writes: RepositoryWrite[] = [{ store: 'wrongNotes', value: restored }];
      if (restored.photoAssetId) {
        const photo = await studyRepository.get('photoAssets', restored.photoAssetId);
        if (!photo.ok || !photo.value) return false;
        if (photo.value.deletedAt) {
          const activePhoto = { ...photo.value };
          delete activePhoto.deletedAt;
          writes.unshift({ store: 'photoAssets', value: activePhoto });
        }
      }
      const result =
        writes.length > 1
          ? await studyRepository.putBatch(writes)
          : await studyRepository.put('wrongNotes', restored);
      if (!result.ok) return false;
      return reload();
    },
    [reload, studyRepository],
  );

  const reviewNote = useCallback(
    async (
      noteId: UUID,
      action: 'review' | 'master' | 'relearn' | 'remembered' | 'uncertain' | 'forgotten',
    ): Promise<boolean> => {
      const current = await studyRepository.get('wrongNotes', noteId);
      if (!current.ok || !current.value || current.value.deletedAt) return false;
      const timestamp = now();
      const updated: WrongNote =
        action === 'relearn'
          ? beginWrongNoteRelearning(current.value, timestamp)
          : {
              ...current.value,
              reviewCount: current.value.reviewCount + (action === 'master' ? 0 : 1),
              ...(['remembered', 'uncertain', 'forgotten'].includes(action)
                ? {
                    lastRecall: action as 'remembered' | 'uncertain' | 'forgotten',
                    lastReviewedAt: timestamp,
                  }
                : {}),
              ...(action === 'review' ? { lastReviewedAt: timestamp } : {}),
              updatedAt: timestamp,
              revision: current.value.revision + 1,
            };
      if (['remembered', 'uncertain', 'forgotten'].includes(action)) {
        delete updated.relearningAt;
        delete updated.masteredAt;
      }
      if (action === 'master') updated.masteredAt = timestamp;
      const result = await studyRepository.put('wrongNotes', updated);
      if (!result.ok) return false;
      if (action === 'master') {
        sendProductEvent(analytics, 'wrong_note_mastered', {
          countBucket: countBucket(
            state.notes.filter((note) => note.masteredAt !== undefined).length + 1,
          ),
        });
      }
      return reload();
    },
    [analytics, reload, state.notes, studyRepository],
  );

  const startReviewSession = useCallback(
    (mode: 'random' | 'subject' | 'unmastered', count: number): void => {
      sendProductEvent(analytics, 'review_started', {
        mode,
        countBucket: countBucket(count),
      });
    },
    [analytics],
  );

  const refreshCommittedPlan = useCallback(
    async (plan: Plan, operation: PendingPlanRefresh['operation']): Promise<void> => {
      setPendingPlanRefresh({ operation, version: plan.version });
      try {
        await reload();
      } catch (error) {
        const report = errors.capture(
          error instanceof Error ? error : new Error('Plan refresh failed'),
          { boundary: 'study_repository', feature: 'plan_refresh_after_commit' },
        );
        setState((current) => ({
          ...current,
          status: 'error',
          ...(report.ok ? { errorReference: report.value.referenceId } : {}),
        }));
      }
    },
    [errors, reload],
  );

  const confirmPlan = useCallback(
    async (draft: PlanDraft): Promise<Plan | undefined> => {
      const existing = await studyRepository.list('plans', { examId: draft.input.examId });
      if (!existing.ok) {
        errors.capture(new Error('Plan history read failed'), {
          boundary: 'study_repository',
          feature: 'plan',
          reason: existing.reason,
        });
        return undefined;
      }
      const previous = existing.value
        .filter((plan) => plan.status === 'active')
        .sort((left, right) => right.version - left.version)[0];
      const timestamp = now();
      const version = materializePlanVersion(draft, {
        planId: id(),
        history: existing.value,
        itemId: () => id(),
        generatedAt: timestamp,
        confirmedAt: timestamp,
        ...(previous === undefined ? {} : { previous }),
      });
      const saved = await studyRepository.putBatch([
        ...(version.superseded === undefined
          ? []
          : [{ store: 'plans' as const, value: version.superseded }]),
        { store: 'plans', value: version.active },
        ...version.items.map((item) => ({ store: 'planItems' as const, value: item })),
      ]);
      if (!saved.ok) {
        errors.capture(new Error('Plan version write failed'), {
          boundary: 'study_repository',
          feature: 'plan',
          reason: saved.reason,
        });
        return undefined;
      }
      const exam = state.exams.find((candidate) => candidate.id === draft.input.examId);
      if (exam) {
        sendProductEvent(
          analytics,
          previous === undefined ? 'plan_confirmed' : 'plan_recalculated',
          {
            dDayBucket: dDayBucket(calculateDDayAt(timestamp, exam.timezone, exam.examDate).days),
            result: 'success',
          },
        );
      }
      await refreshCommittedPlan(version.active, 'save');
      return version.active;
    },
    [analytics, errors, refreshCommittedPlan, state.exams, studyRepository],
  );

  const reconcileCumulativeMilestones = useCallback(
    async (examId: UUID): Promise<boolean> => {
      const [logResult, noteResult, planResult, planItemResult, sessionResult, milestoneResult] =
        await Promise.all([
          studyRepository.list('studyLogs', { examId, includeDeleted: true }),
          studyRepository.list('wrongNotes', { examId, includeDeleted: true }),
          studyRepository.list('plans', { examId }),
          studyRepository.list('planItems'),
          studyRepository.list('mogakSessions', { examId }),
          studyRepository.list('milestones', { examId }),
        ]);
      if (
        !logResult.ok ||
        !noteResult.ok ||
        !planResult.ok ||
        !planItemResult.ok ||
        !sessionResult.ok ||
        !milestoneResult.ok
      ) {
        errors.capture(new Error('Cumulative milestone inputs unavailable'), {
          boundary: 'study_repository',
          feature: 'milestone_catalog',
        });
        return false;
      }
      const activePlans = planResult.value.filter((plan) => plan.status === 'active');
      const completedPlanIds = activePlans
        .filter((plan) => {
          const items = planItemResult.value.filter((item) => item.planId === plan.id);
          return items.length === 0 || items.every((item) => item.status === 'done');
        })
        .map((plan) => plan.id);
      const expected = cumulativeMilestoneCandidates({
        examId,
        logs: logResult.value,
        masteredNoteIds: noteResult.value
          .filter((note) => note.deletedAt === undefined && note.masteredAt !== undefined)
          .map((note) => note.id),
        completedPlanIds,
        roomSessionIds: sessionResult.value
          .filter(
            (session) =>
              session.activeMinutes > 0 &&
              session.outcome !== 'kicked' &&
              session.outcome !== 'disconnected',
          )
          .map((session) => session.id),
      });
      const reconciled = milestoneReconciliation({
        existing: milestoneResult.value,
        expected,
      });
      for (const candidate of reconciled.award) {
        await studyRepository.put('milestones', {
          id: id(),
          key: candidate.key,
          ...(candidate.examId === undefined ? {} : { examId: candidate.examId }),
          ...(candidate.sourceEntityId === undefined
            ? {}
            : { sourceEntityId: candidate.sourceEntityId }),
          earnedAt: now(),
        });
      }
      for (const milestone of reconciled.revoke) {
        await studyRepository.deletePermanent('milestones', milestone.id);
      }
      return reconciled.award.length > 0 || reconciled.revoke.length > 0;
    },
    [errors, studyRepository],
  );

  const completePlanItem = useCallback(
    async (planItemId: UUID, done: boolean): Promise<boolean> => {
      const current = await studyRepository.get('planItems', planItemId);
      if (!current.ok || !current.value) return false;
      const plan = await studyRepository.get('plans', current.value.planId);
      if (!plan.ok || !plan.value || plan.value.status !== 'active') return false;
      const timestamp = now();
      const existingLogs = await studyRepository.list('studyLogs', { includeDeleted: true });
      if (!existingLogs.ok) return false;
      const existing = existingLogs.value.find((log) => log.planItemId === planItemId);
      const writes: RepositoryWrite[] = [
        { store: 'planItems', value: { ...current.value, status: done ? 'done' : 'planned' } },
      ];
      if (done) {
        const subject = await studyRepository.get('subjects', current.value.subjectId);
        const exam = await studyRepository.get('exams', plan.value.examId);
        if (!subject.ok || !subject.value || !exam.ok || !exam.value) return false;
        const log: StudyLog = {
          id: existing?.id ?? id(),
          examId: plan.value.examId,
          subjectId: current.value.subjectId,
          studyDate: studyDateAt(timestamp, exam.value.timezone, 4),
          round: current.value.round,
          unitStart: current.value.unitStart,
          unitEnd: current.value.unitEnd,
          source: 'plan',
          planItemId,
          createdAt: existing?.createdAt ?? timestamp,
          updatedAt: timestamp,
          revision: (existing?.revision ?? 0) + 1,
        };
        writes.push({ store: 'studyLogs', value: log });
      } else if (existing && !existing.deletedAt) {
        writes.push({
          store: 'studyLogs',
          value: {
            ...existing,
            deletedAt: timestamp,
            updatedAt: timestamp,
            revision: existing.revision + 1,
          },
        });
      }
      const saved = await studyRepository.putBatch(writes);
      if (!saved.ok) return false;
      await reconcileCumulativeMilestones(plan.value.examId);
      return reload();
    },
    [reconcileCumulativeMilestones, reload, studyRepository],
  );

  const saveMogakSession = useCallback(
    async (session: MogakSession): Promise<boolean> => {
      const saved = await studyRepository.put('mogakSessions', session);
      if (!saved.ok) return false;
      await reconcileCumulativeMilestones(session.examId);
      return reload();
    },
    [reconcileCumulativeMilestones, reload, studyRepository],
  );

  const cumulativeInputVersion = useMemo(
    () =>
      JSON.stringify({
        exams: state.exams.map((exam) => exam.id),
        logs: state.logs.map((log) => [log.id, log.updatedAt, log.deletedAt]),
        notes: state.notes.map((note) => [
          note.id,
          note.updatedAt,
          note.masteredAt,
          note.deletedAt,
        ]),
        plans: state.plans.map((plan) => [plan.id, plan.status]),
        planItems: state.planItems.map((item) => [item.id, item.status]),
        milestones: state.milestones.map((milestone) => milestone.id),
      }),
    [state.exams, state.logs, state.milestones, state.notes, state.planItems, state.plans],
  );

  useEffect(() => {
    if (state.status !== 'ready' || state.exams.length === 0) return;
    let active = true;
    void Promise.all(state.exams.map((exam) => reconcileCumulativeMilestones(exam.id))).then(
      (changes) => {
        if (active && changes.some(Boolean)) void reload();
      },
    );
    return () => {
      active = false;
    };
  }, [cumulativeInputVersion, reconcileCumulativeMilestones, reload, state.exams, state.status]);

  const restorePlan = useCallback(
    async (planId: UUID): Promise<boolean> => {
      const requested = await studyRepository.get('plans', planId);
      if (!requested.ok || !requested.value || requested.value.status === 'active') return false;
      const plans = await studyRepository.list('plans', { examId: requested.value.examId });
      if (!plans.ok) return false;
      const writes: RepositoryWrite[] = [
        { store: 'plans', value: { ...requested.value, status: 'active' as const } },
        ...plans.value
          .filter((plan) => plan.id !== requested.value?.id && plan.status === 'active')
          .map((plan) => ({
            store: 'plans' as const,
            value: { ...plan, status: 'superseded' as const },
          })),
      ];
      const saved = await studyRepository.putBatch(writes);
      if (!saved.ok) return false;
      await refreshCommittedPlan(requested.value, 'restore');
      return true;
    },
    [refreshCommittedPlan, studyRepository],
  );

  const getPhoto = useCallback(
    async (photoId: UUID): Promise<PhotoAsset | undefined> => {
      const result = await studyRepository.get('photoAssets', photoId);
      return result.ok && result.value && result.value.deletedAt === undefined
        ? result.value
        : undefined;
    },
    [studyRepository],
  );

  const value = useMemo<StudyContextValue>(
    () => ({
      ...state,
      pendingPlanRefresh,
      pendingLogRefresh,
      recentAchievements,
      dismissAchievements,
      reload,
      createExam,
      updateExam,
      archiveExam,
      restoreExam,
      saveLog,
      saveLogBatch,
      updateLog,
      deleteLog,
      restoreLog,
      saveNote,
      updateNote,
      deleteNote,
      restoreNote,
      reviewNote,
      startReviewSession,
      confirmPlan,
      restorePlan,
      completePlanItem,
      saveMogakSession,
      getPhoto,
    }),
    [
      archiveExam,
      createExam,
      confirmPlan,
      completePlanItem,
      deleteLog,
      deleteNote,
      dismissAchievements,
      getPhoto,
      recentAchievements,
      reload,
      restoreExam,
      restoreLog,
      restoreNote,
      restorePlan,
      saveMogakSession,
      reviewNote,
      saveLog,
      saveLogBatch,
      saveNote,
      startReviewSession,
      state,
      pendingPlanRefresh,
      pendingLogRefresh,
      updateLog,
      updateNote,
      updateExam,
    ],
  );
  return <StudyContext.Provider value={value}>{children}</StudyContext.Provider>;
}

export function useStudy(): StudyContextValue {
  const context = useContext(StudyContext);
  if (!context) throw new Error('StudyProvider is required');
  return context;
}
